import { clsx } from 'clsx'
import { NavLink } from 'react-router-dom-v5-compat'

import {
  ChannelAvatar,
  Flex,
  LockIcon,
  Sidebar,
  Text,
  WarningIcon,
  Tooltip,
  useSidebar,
  Link,
  CriticalIcon,
} from '@buffer-mono/popcorn'

import { isFacebookGroup } from '~publish/helpers/channels'
import { commentsChannel } from '~publish/legacy/routes'
import { CommentsCount } from '~publish/pages/Comments/components/CommentsCount'
import type { Channel } from '~publish/gql/graphql'

import { getEngageUrl, getTooltipContent, requiresRefreshing } from './helpers'
import { useCommentsConfig } from '../../../pages/Comments/CommentsChannelConfigContext'

import styles from './ChannelNav.module.css'

function ChannelSuffix({
  channel,
  children,
}: {
  channel: Channel
  children?: React.ReactNode
}): JSX.Element {
  if (channel.isDisconnected) {
    return (
      <Tooltip content="This channel is disconnected">
        <WarningIcon size="small" color="critical" />
      </Tooltip>
    )
  }

  if (channel.isLocked) {
    return (
      <Tooltip content="This channel is locked">
        <LockIcon size="small" />
      </Tooltip>
    )
  }

  if (requiresRefreshing(channel)) {
    return (
      <Tooltip content="This channel requires refreshing">
        <CriticalIcon size="small" color="critical" />
      </Tooltip>
    )
  }

  return <div className={styles.commentCount}>{children}</div>
}

function ChannelNavItemContent({ channel }: { channel: Channel }): JSX.Element {
  const serverUrl = isFacebookGroup(channel) ? null : channel.serverUrl
  const additionalInfo = channel.locationData?.location ?? serverUrl

  return (
    <Flex direction="column" gap="2xs">
      <Text weight="medium" data-testid="channel-name" lineHeight="tight">
        {channel.name}
      </Text>
      {additionalInfo && (
        <Text size="sm" color="subtle" lineHeight="tight">
          {additionalInfo}
        </Text>
      )}
    </Flex>
  )
}

type ChannelNavigationItemProps = {
  channel: Channel
  className?: string
  commentCounts: number | null
}

export function ChannelNavItem({
  channel,
  className,
  commentCounts,
}: ChannelNavigationItemProps): JSX.Element {
  const { state } = useSidebar()
  const {
    config: { isSupportedInEngage, isChannelSupported },
  } = useCommentsConfig()

  const isChannelFromEngage = isSupportedInEngage(channel)
  const shouldDisableChannel = !isChannelSupported(channel)

  const buttonProps = {
    size: 'large' as const,
    className: clsx(
      shouldDisableChannel && styles.disabled,
      isChannelFromEngage && styles.engageLink,
    ),
    prefix: (
      <ChannelAvatar
        alt={channel.service}
        channel={channel.service}
        src={channel.avatar}
        size="small"
      />
    ),
    suffix: (
      <ChannelSuffix channel={channel}>
        {commentCounts && <CommentsCount count={commentCounts} />}
      </ChannelSuffix>
    ),
  }

  const Item = (
    <Sidebar.ListItem
      id={channel.id}
      className={clsx(styles.channel, className)}
    >
      {isChannelFromEngage && (
        <Sidebar.Button
          {...buttonProps}
          as={Link}
          href={getEngageUrl(channel.id)}
        >
          <ChannelNavItemContent channel={channel} />
        </Sidebar.Button>
      )}
      {!isChannelFromEngage && (
        <Sidebar.Button
          {...buttonProps}
          as={NavLink}
          to={commentsChannel.getRoute({ channelId: channel.id })}
        >
          <ChannelNavItemContent channel={channel} />
        </Sidebar.Button>
      )}

      {/* This is the count badge when the sidebar is collapsed */}
      {commentCounts !== null &&
        commentCounts !== undefined &&
        commentCounts > 0 && (
          <CommentsCount
            count={commentCounts}
            className={clsx(
              styles.collapsedBadge,
              state === 'expanded' && styles.collapsedBadgeHidden,
            )}
          />
        )}
    </Sidebar.ListItem>
  )

  if (shouldDisableChannel) {
    return (
      <Tooltip
        content={getTooltipContent(channel.service, channel.type)}
        side="right"
        delay={400}
      >
        {Item}
      </Tooltip>
    )
  }

  return Item
}
